/**
 * Aktivní vs plný katalog materiálu (Komponenty).
 *
 * - **active** — firemní / „zařazené“ položky (`components.is_active = TRUE`)
 * - **all** — celý známý katalog včetně neaktivních SAP
 *
 * Default list/tree endpointy a pickery vrací `active`, pokud klient nepožádá `scope=all`.
 */

export const MATERIAL_CATALOG_SCOPES = ['active', 'all'] as const;
export type MaterialCatalogScope = (typeof MATERIAL_CATALOG_SCOPES)[number];

export const MATERIAL_CATALOG_DEFAULT_SCOPE: MaterialCatalogScope = 'active';

export function parseMaterialCatalogScope(
  raw: string | null | undefined,
  fallback: MaterialCatalogScope = MATERIAL_CATALOG_DEFAULT_SCOPE,
): MaterialCatalogScope {
  const v = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (v === 'all' || v === 'full') return 'all';
  if (v === 'active' || v === 'assigned') return 'active';
  return fallback;
}

/** Platná ceníková nabídka jednoho dodavatele u SAP (overlay ze Správy ceníku). */
export interface MaterialCatalogSupplierOffer {
  supplierId: string;
  supplierName: string;
  unitPrice: number;
  currency: string;
}

type CatalogSupplierFields = {
  dodavatel?: string | null;
  dodavatele?: MaterialCatalogSupplierOffer[] | null;
  cena?: string | null;
  mena?: string | null;
};

function compareSupplierNamesCs(a: string, b: string): number {
  return a.localeCompare(b, 'cs', { sensitivity: 'base' });
}

/** Všichni dodavatelé produktu (z `dodavatele`, fallback `dodavatel`). */
export function catalogItemSupplierNames(item: CatalogSupplierFields | null | undefined): string[] {
  const fromList = (item?.dodavatele ?? [])
    .map((s) => String(s.supplierName ?? '').trim())
    .filter(Boolean);
  if (fromList.length) {
    return [...new Set(fromList)].sort(compareSupplierNamesCs);
  }
  const legacy = String(item?.dodavatel ?? '').trim();
  return legacy ? [legacy] : [];
}

/** Nabídky k zobrazení (ceny + jména). Fallback na nejlevnější overlay, pokud pole `dodavatele` chybí. */
export function catalogItemSupplierOffers(
  item: CatalogSupplierFields | null | undefined,
): MaterialCatalogSupplierOffer[] {
  const list = (item?.dodavatele ?? []).filter((s) => String(s.supplierName ?? '').trim());
  if (list.length) {
    return [...list].sort((a, b) => compareSupplierNamesCs(a.supplierName, b.supplierName));
  }
  const name = String(item?.dodavatel ?? '').trim();
  if (!name) return [];
  const n = Number(String(item?.cena ?? '').trim().replace(/\s/g, '').replace(',', '.'));
  return [
    {
      supplierId: '',
      supplierName: name,
      unitPrice: Number.isFinite(n) ? n : 0,
      currency: String(item?.mena ?? '').trim() || 'CZK',
    },
  ];
}

/** Nejlevnější nabídka (cena, pak název cs, pak id) — stejné pravidlo jako nákupní quote. */
export function cheapestCatalogSupplierOffer(
  offers: readonly MaterialCatalogSupplierOffer[],
): MaterialCatalogSupplierOffer | undefined {
  if (!offers.length) return undefined;
  return [...offers].sort((a, b) => {
    const priceDiff = a.unitPrice - b.unitPrice;
    if (priceDiff !== 0) return priceDiff;
    const nameCmp = compareSupplierNamesCs(a.supplierName, b.supplierName);
    if (nameCmp !== 0) return nameCmp;
    return String(a.supplierId).localeCompare(String(b.supplierId));
  })[0];
}

/** Flat DTO řádku katalogu (správa aktivních / plný seznam). */
export interface ComponentCatalogItemDto {
  sap: string;
  isActive: boolean;
  /** FK na `material_product_category` (strom podle typů objektů). */
  categoryId: string | null;
  hlavniKategorie: string | null;
  subkategorie: string | null;
  rozvetveni1: string | null;
  rozvetveni2: string | null;
  rozvetveni3: string | null;
  rozvetveni4: string | null;
  zdrojovyNazev: string | null;
  jednotka: string | null;
  /** Nejlevnější platný dodavatel ze Správy ceníku (API overlay; DB sloupec odstraněn migrací 144). */
  dodavatel: string | null;
  /**
   * Všichni aktivní dodavatelé s platnou cenou u tohoto SAP (`material_supplier_prices`).
   * Prázdné = žádný ceník. `dodavatel`/`cena` zůstávají nejlevnější overlay.
   */
  dodavatele: MaterialCatalogSupplierOffer[];
  /** Nejlevnější platná jednotková cena ze Správy ceníku (API overlay; DB sloupec odstraněn migrací 144). */
  cena: string | null;
  mena: string | null;
  /** Složená cesta zařazení pro UI (neprázdné úrovně). */
  pathLabel: string;
  /** Produkt má nahraný obrázek v object storage (`GET …/components/:sap/image`). */
  hasImage: boolean;
}

/** Výsledek importu aktivního katalogu (Excel/CSV zařazení). */
export interface ComponentCatalogImportResult {
  /** Počet unikátních SAP v souboru. */
  rowCount: number;
  createdCount: number;
  updatedCount: number;
  /** Kolik existujících neaktivních bylo aktivováno. */
  activatedCount: number;
  /** Hard sync: deaktivované SAP chybějící v souboru (Obecný). */
  deactivatedCount?: number;
  /** Hard sync: odebraná doporučení (SAP×fáze) chybějící v souboru. */
  removedRecommendedCount?: number;
  /** Duplicitní SAP v souboru (ponechán poslední řádek). */
  duplicateSapCount: number;
  sheetName: string;
  /** Všechny zpracované listy (doporučený multi-sheet import). */
  sheetNames?: string[];
  /** Ukázka nově vytvořených SAP (max 50). */
  createdSaps: string[];
  /** Ukázka aktualizovaných SAP (max 50). */
  updatedSaps: string[];
  /** Ukázka deaktivovaných SAP (max 50). */
  deactivatedSaps?: string[];
  /** Počet upsertů do material_supplier_prices (Dodavatel+Cena z importu). */
  supplierPricesUpserted?: number;
  /** Řádky s Dodavatel bez Cena (ceník se nezapsal). */
  supplierPricesSkippedNoPrice?: number;
}
